import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Search, 
  Eye, 
  RotateCcw,
  FileText } from '../components/animated-icons';
import { Pencil, Filter, Calendar, Inbox } from 'lucide-react';
import './Invoices.css';


const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    fromDate: '',
    toDate: '',
    search: ''
  });
  const searchInputRef = useRef(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, filters, rowsPerPage]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: rowsPerPage,
        ...filters
      };

      const response = await api.get('/invoices', { params });
      setInvoices(response.data.invoices);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const wasSearchInput = name === 'search';
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));

    if (wasSearchInput && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      paymentStatus: '',
      fromDate: '',
      toDate: '',
      search: ''
    });
  };

  // if (loading && invoices.length === 0) return <Loading />;

  return (
    <div className="invoices-dashboard">
      {/* ─── Modern Page Header ─── */}
      <div className="invoices-header-modern">
        <div className="ih-title-group">
          <div className="ih-icon-wrapper">
            <FileText size={24} />
          </div>
          <div className="ih-text">
            <h1>Invoices</h1>
            <p>Manage and track your business billing</p>
          </div>
        </div>
        {true && (
          <Link to="/invoice/create" className="btn-create-invoice">
            <Plus size={18} /> New Invoice
          </Link>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* ─── Search & Filters Card ─── */}
      <div className="invoices-filters-card">
        <div className="filters-top-row">
          <div className="modern-search-bar">
            <Search size={18} className="modern-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              name="search"
              placeholder="Search by invoice number or customer name..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filters-options">
            <div className="filter-pill">
              <Filter size={14} />
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="filter-pill">
              <Filter size={14} />
              <select name="paymentStatus" value={filters.paymentStatus} onChange={handleFilterChange}>
                <option value="">All Payments</option>
                <option value="paid">Paid</option>
                <option value="due">Due</option>
                <option value="partial">Partial</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div className="filter-pill">
              <Calendar size={14} />
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                title="From Date"
              />
            </div>
            <div className="filter-pill">
              <Calendar size={14} />
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                title="To Date"
              />
            </div>
            <button className="btn-reset-filters" onClick={resetFilters}>
              <RotateCcw size={14} /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* ─── Modern Table Card ─── */}
      <div className="invoices-list-card">
        <div className="list-header">
          <h3>
            All Invoices
            <span className="count-badge">{pagination.total}</span>
          </h3>
        </div>
        
        <div className="table-responsive">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th style={{textAlign: 'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length > 0 ? (
                invoices.map(invoice => (
                  <tr key={invoice._id}>
                    <td>
                      <div className="td-invoice">
                        <div className="inv-icon"><FileText size={20} /></div>
                        <div className="inv-details">
                          <span className="inv-number">{invoice.invoiceNumber}</span>
                          <span className="inv-date">{formatDate(invoice.invoiceDate)}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="customer-name">{invoice.customerDetails?.name || 'Unknown'}</span>
                    </td>
                    <td>
                      <span className="amount-value">{formatCurrency(invoice.grandTotal)}</span>
                    </td>
                    <td>
                      <span className={`status-pill status-${invoice.paymentStatus.toLowerCase()}`}>
                        {invoice.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <span className={`status-pill status-${invoice.status.toLowerCase()}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions" style={{justifyContent: 'flex-end'}}>
                        <Link to={`/invoice/${invoice._id}`} className="btn-icon view" title="View Invoice">
                          <Eye size={16} />
                        </Link>
                        {!invoice.isLocked && invoice.status === 'active' && (
                          <Link to={`/invoice/${invoice._id}/edit`} className="btn-icon edit" title="Edit Invoice">
                            <Pencil size={16} />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="empty-state">
                      <div className="empty-icon-wrapper">
                        <Inbox size={32} />
                      </div>
                      <h3>No invoices found</h3>
                      <p>You haven't created any invoices matching these filters yet. Create your first invoice to get started.</p>
                      {true && (
                        <Link to="/invoice/create" className="btn-create-invoice">
                          <Plus size={18} /> Create Invoice
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── List Footer ─── */}
        <div className="list-footer">
          <div className="rows-selector">
            Show
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </div>
          
          {pagination.pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoices;
